import React, { useState } from 'react';
import { SelectableTest } from '../../types/trends';

interface TestSelectorProps {
  availableTests: SelectableTest[];
  selectedTests: string[];
  onChange: (selected: string[]) => void;
  onClearSaved?: () => void;
}

export const TestSelector: React.FC<TestSelectorProps> = ({
  availableTests,
  selectedTests,
  onChange,
  onClearSaved
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(false);

  const filteredTests = availableTests.filter(test =>
    test.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    test.testName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedTests = showAll ? filteredTests : filteredTests.slice(0, 6);

  const handleToggle = (testName: string) => {
    if (selectedTests.includes(testName)) {
      onChange(selectedTests.filter(t => t !== testName));
    } else {
      onChange([...selectedTests, testName]);
    }
  };

  const handleSelectAll = () => {
    if (selectedTests.length === filteredTests.length) {
      onChange([]);
    } else {
      onChange(filteredTests.map(t => t.testName));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Select Tests</h3>
      
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search tests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-3 flex justify-between items-center">
        <div className="flex gap-3">
          <button
            onClick={handleSelectAll}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            {selectedTests.length === filteredTests.length ? 'Clear all' : 'Select all'}
          </button>
          {onClearSaved && (
            <button
              onClick={onClearSaved}
              className="text-sm text-gray-600 hover:text-gray-700"
              title="Clear saved test selection preferences"
            >
              Reset saved
            </button>
          )}
        </div>
        <span className="text-sm text-gray-500">
          {selectedTests.length} selected
        </span>
      </div>

      {/* Test List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {displayedTests.map(test => (
          <label
            key={test.testName}
            className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
              !test.available ? 'opacity-50' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={selectedTests.includes(test.testName)}
              onChange={() => handleToggle(test.testName)}
              disabled={!test.available}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <div className="ml-3 flex-1">
              <span className="text-sm font-medium text-gray-900">
                {test.displayName}
              </span>
              {test.lastValue && (
                <span className="ml-2 text-xs text-gray-500">
                  Last: {test.lastValue} {test.units}
                </span>
              )}
            </div>
          </label>
        ))}
      </div>

      {/* Show More/Less */}
      {filteredTests.length > 6 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 text-sm text-blue-600 hover:text-blue-700"
        >
          {showAll ? 'Show less' : `Show ${filteredTests.length - 6} more`}
        </button>
      )}

      {/* Common Test Groups */}
      <div className="mt-4 pt-4 border-t">
        <p className="text-xs text-gray-500 mb-2">Quick selections:</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onChange(['hemoglobin', 'hematocrit', 'wbc', 'platelet_count'])}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
          >
            Blood Count
          </button>
          <button
            onClick={() => onChange(['glucose', 'cholesterol_total'])}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
          >
            Metabolic
          </button>
          <button
            onClick={() => onChange(['alt', 'creatinine'])}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
          >
            Organ Function
          </button>
        </div>
      </div>
    </div>
  );
};